import { ArrowRight, Sparkles, Star } from "lucide-react";
import { featuredProducts } from "@/data/featuredProducts";

/**
 * Compact promo banner displayed right under the hero — above the fold on most screens.
 * Promotes the lead product (Le Guide des Jeunes Héros) with cover, price and CTA.
 */
export default function HeroPromoBanner() {
    const p = featuredProducts[0];
    if (!p) return null;

    return (
        <section
            data-testid="hero-promo-banner"
            className="relative -mt-8 sm:-mt-12 pb-10 sm:pb-16"
        >
            <div className="container-wide">
                <a
                    href={p.cta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-promo-cta"
                    className="group relative grid sm:grid-cols-[auto_1fr_auto] items-center gap-5 sm:gap-7 rounded-[2rem] bg-gradient-to-br from-ink-900 via-ink-900 to-brand-950 text-white px-5 sm:px-8 py-5 sm:py-6 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-20px_rgba(20,184,166,0.4)]"
                >
                    {/* Decorative blobs */}
                    <div className="pointer-events-none absolute -top-20 -right-10 h-[220px] w-[220px] rounded-full bg-brand-500/30 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-20 -left-10 h-[200px] w-[200px] rounded-full bg-pinky-500/20 blur-3xl" />

                    {/* Cover thumb */}
                    <div className="relative shrink-0 z-10">
                        <div className="absolute -top-2 -left-2 inline-flex items-center gap-1 rounded-full bg-pinky-500 text-white text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-1 shadow-lg z-10">
                            <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
                            Lancement
                        </div>
                        <img
                            src={p.cover}
                            alt={p.name}
                            className="h-24 sm:h-28 w-auto rounded-xl shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)] object-contain bg-ink-950"
                            loading="eager"
                        />
                    </div>

                    {/* Text */}
                    <div className="relative z-10 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-300">
                            <span className="inline-flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                                ))}
                            </span>
                            <span>Best-seller · 7-10 ans</span>
                        </div>
                        <p className="mt-2 font-display font-bold text-lg sm:text-2xl leading-tight">
                            {p.name}
                        </p>
                        <p className="mt-1 text-xs sm:text-sm text-ink-300 max-w-md hidden sm:block">
                            {p.description}
                        </p>
                    </div>

                    {/* Price + CTA */}
                    <div className="relative z-10 flex items-center gap-4 sm:gap-5 sm:flex-col sm:items-end sm:text-right shrink-0">
                        <div>
                            <div className="flex items-baseline gap-2 justify-end">
                                <span className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
                                    {p.price}
                                </span>
                                {p.oldPrice && (
                                    <span className="font-display text-base sm:text-lg text-ink-400 line-through decoration-pinky-500 decoration-2">
                                        {p.oldPrice}
                                    </span>
                                )}
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-brand-300 font-semibold mt-0.5">
                                Prix lancement
                            </p>
                        </div>
                        <span className="inline-flex items-center justify-center rounded-full bg-white text-brand-700 font-semibold px-5 py-3 text-sm shadow-lg transition-transform group-hover:scale-105">
                            {p.cta.label}
                            <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2.5} />
                        </span>
                    </div>
                </a>
            </div>
        </section>
    );
}
